import { base, baseSepolia } from "viem/chains";
import { firebaseConfig, vapidKeyConfig } from "./firebaseConfig";
// ⚠️ 直接路径，**不要**改回桶文件 `@api/osp`：那个桶会加载 `appOspSecurity.ts`，
// 而它在模块顶层就读 `config.osp.*` —— 于是 config ↔ 桶 形成初始化期循环依赖，
// 症状是 `ReferenceError: Cannot access 'config' before initialization`。
// 见 docs/engineering/barrel-file-guidelines.md。
import { Environment } from "@api/osp/constants";

import type { Config } from "./type";

if (
  process.env.NEXT_PUBLIC_ENV === Environment.beta ||
  process.env.NEXT_PUBLIC_ENV === Environment.pre ||
  process.env.NEXT_PUBLIC_ENV === Environment.dev
) {
  process.env["NODE_TLS_REJECT_UNAUTHORIZED"] = "0";
}
const web3ChainConfig = {
  dev: {
    ...baseSepolia,
  },
  beta: {
    ...baseSepolia,
  },
  pre: {
    ...base,
  },
  prod: {
    ...base,
  },
};

const questApiBaseUrl = {
  dev: "https://api.quests.dev.dipbit.xyz",
  beta: "https://api.quests.beta.dipbit.xyz",
  pre: "https://quest.pre.ospprotocol.xyz",
  prod: "https://quest.edwealth.ai",
};

/** window 不存在时（如 SSR）使用的 baseUrl，按需修改 */
const chatApiBaseUrlWhenNoWindow = {
  dev: "https://ai-dev.jiabanmoyu.com/api",
  beta: "https://ai-beta.jiabanmoyu.com/api",
  pre: "https://ai-pre.jiabanmoyu.com/api",
  prod: "https://ai.edwealth.ai/api",
};

/** window 存在时（浏览器）使用的 baseUrl，按需修改 */
const chatApiBaseUrlWhenWindow = {
  dev: "https://perplexica-dev.ospprotocol.xyz/api",
  beta: "https://perplexica-beta.ospprotocol.xyz/api",
  pre: "https://perplexica-pre.ospprotocol.xyz/api",
  prod: "https://ai.edwealth.ai/api",
};

const chatApiBaseUrl: typeof chatApiBaseUrlWhenWindow = new Proxy(
  chatApiBaseUrlWhenWindow,
  {
    get(_target, prop: string | symbol) {
      const source =
        typeof window === "undefined"
          ? chatApiBaseUrlWhenNoWindow
          : chatApiBaseUrlWhenWindow;
      return (source as Record<string | symbol, unknown>)[prop];
    },
  },
);

export const chatClientApiBaseUrl = {
  dev: "https://perplexica-dev.ospprotocol.xyz/api",
  beta: "https://perplexica-beta.ospprotocol.xyz/api",
  pre: "https://perplexica-pre.ospprotocol.xyz/api",
  prod: "https://ai.edwealth.ai/api",
} as any;

const strApiBaseUrl = {
  dev: "https://strapi.edwealth.ai/api",
  beta: "https://strapi.edwealth.ai/api",
  pre: "https://strapi.edwealth.ai/api",
  prod: "https://strapi.edwealth.ai/api",
};

const strApiToken = {
  dev: "586280d21e5cda857b7ebbdcdec659c38ff6cc099b600f0b01f600c79a74730f321553994a5b7657fd08f66d6de16326b358b3be5ae3722c570df6c20f904414d2bb83e5ad8aa7a66eac4d30b99aa1908a83cf358bbc6ed66bf9e951051df0885b7471735b78fa7d132e51ca5adba969cc52f301f4485dab2f6d558177b5de41",
  beta: "586280d21e5cda857b7ebbdcdec659c38ff6cc099b600f0b01f600c79a74730f321553994a5b7657fd08f66d6de16326b358b3be5ae3722c570df6c20f904414d2bb83e5ad8aa7a66eac4d30b99aa1908a83cf358bbc6ed66bf9e951051df0885b7471735b78fa7d132e51ca5adba969cc52f301f4485dab2f6d558177b5de41",
  pre: "586280d21e5cda857b7ebbdcdec659c38ff6cc099b600f0b01f600c79a74730f321553994a5b7657fd08f66d6de16326b358b3be5ae3722c570df6c20f904414d2bb83e5ad8aa7a66eac4d30b99aa1908a83cf358bbc6ed66bf9e951051df0885b7471735b78fa7d132e51ca5adba969cc52f301f4485dab2f6d558177b5de41",
  prod: "586280d21e5cda857b7ebbdcdec659c38ff6cc099b600f0b01f600c79a74730f321553994a5b7657fd08f66d6de16326b358b3be5ae3722c570df6c20f904414d2bb83e5ad8aa7a66eac4d30b99aa1908a83cf358bbc6ed66bf9e951051df0885b7471735b78fa7d132e51ca5adba969cc52f301f4485dab2f6d558177b5de41",
};

const aiStreamApiBaseUrl = {
  dev: "https://ai-agent-dev.ospprotocol.xyz",
  beta: "https://ai-agent-beta.ospprotocol.xyz",
  pre: "https://ai-agent-pre.opensocial.fun",
  prod: "https://ai-agent.edwealth.ai",
};

export const config: Config = {
  osp: {
    appId: process.env.NEXT_PUBLIC_APP_ID || "",
    environment:
      (process.env.NEXT_PUBLIC_ENV as Environment) || Environment.dev,
  },
  api: {
    baseUrl: {
      quest:
        questApiBaseUrl[
          (process.env.NEXT_PUBLIC_ENV as keyof typeof questApiBaseUrl) ||
            "prod"
        ] || questApiBaseUrl.prod,
      chat:
        chatApiBaseUrl[
          (process.env.NEXT_PUBLIC_ENV as keyof typeof chatApiBaseUrl) || "prod"
        ] || chatApiBaseUrl.prod,
      strApi:
        strApiBaseUrl[
          (process.env.NEXT_PUBLIC_ENV as keyof typeof strApiBaseUrl) || "prod"
        ] || strApiBaseUrl.prod,
    },
    saasId: {
      quest: process.env.NEXT_PUBLIC_SAAS_ID ?? "opensocial",
    },
    token: {
      strApi:
        strApiToken[
          (process.env.NEXT_PUBLIC_ENV as keyof typeof strApiToken) || "prod"
        ] || strApiToken.prod,
    },
    ws: {
      url: process.env.NEXT_PUBLIC_WS_PATH
        ? `wss://${process.env.NEXT_PUBLIC_WS_PATH}`
        : "ws://localhost:60000",
    },
    aiStream: {
      baseUrl:
        aiStreamApiBaseUrl[
          (process.env.NEXT_PUBLIC_ENV as keyof typeof aiStreamApiBaseUrl) ||
            "prod"
        ] || aiStreamApiBaseUrl.prod,
    },
  },
  chainConfig:
    web3ChainConfig[
      (process.env.NEXT_PUBLIC_ENV as keyof typeof web3ChainConfig) || "prod"
    ] || web3ChainConfig.prod,
  externalLink: {
    aiChat: process.env.NEXT_PUBLIC_SITE_UR ?? "",
  },
  firebaseConfig:
    firebaseConfig[
      (process.env.NEXT_PUBLIC_ENV as keyof typeof firebaseConfig) || "prod"
    ] || firebaseConfig.prod,
  vapidKey:
    vapidKeyConfig[
      (process.env.NEXT_PUBLIC_ENV as keyof typeof vapidKeyConfig) || "prod"
    ] || vapidKeyConfig.prod,
};
